/**
 * PermissionMatrix — matriz interativa de permissoes `recurso:acao`.
 *
 * Arvore Modulo > Pagina > Secao > Permissao (campo `catalogo.modulos`,
 * montado pelo backend em `permissoes/hierarquia.py`):
 *   [Modulo]  header forte colapsavel + contagem liberadas/total
 *     [Pagina]  sub-header colapsavel indentado + contagem
 *       [Secao] titulo discreto + "Marcar todas"/"Desmarcar todas"
 *         [Permissao] linha: caixa + nome claro + selos + "?" (ajuda)
 *
 * Modulos comecam colapsados, exceto os que ja tem alguma permissao
 * liberada — o gestor ve primeiro o que ja deu. Busca (sem acento) abre os
 * niveis com resultado e procura tambem no texto de ajuda. "So o que esta
 * liberado" esconde o resto — bom para conferir um cargo.
 *
 * Cada permissao tem ate 4 estados (ver PermissionChip.jsx): liberada aqui,
 * vem do cargo, negada (vem do cargo + negacao individual) e nao liberada.
 *
 * Props principais:
 * - `catalogo`: { modulos: [...] }
 * - `selecionados`: Set<string> de codigos liberados diretamente (controlled)
 * - `herdados`: Set<string> de codigos que vem do cargo
 * - `revogados`: Set<string> de codigos negados individualmente (controlled).
 * - `allowRevoke`: boolean — default false. Quando true, clicar numa
 *   permissao que vem do cargo cria a negacao individual (estado `revoked`).
 *   Quando false, as que vem do cargo sao somente leitura.
 * - `onToggle(codigo, novoEstado)`: callback ao alterar. `novoEstado`:
 *   `true` (= "granted"), `false` (= "default"), `"granted"`, `"revoked"`
 *   (so com allowRevoke) ou `"default"` (remove liberacao/negacao).
 * - `inheritedReadOnly`: boolean — DEPRECATED, use `allowRevoke`.
 * - `disabled`: boolean — so leitura (o "?" continua funcionando).
 * - `mostrarBuscaRapida`: boolean — default true
 */
import React, { useEffect, useMemo, useState } from "react";

import PermissionChip from "./PermissionChip.jsx";
import {
  contarConcedidas,
  esconderLabelSecao,
  filtrarConcedidas,
  filtrarModulos,
  indexarPorCodigo,
  normalizarBusca,
  permissoesDaPagina,
  permissoesDoModulo,
  rotulosChipsDaSecao,
} from "./arvore.js";
import {
  BulkButton,
  Chevron,
  Container,
  CountBadge,
  Counter,
  EmptyState,
  FiltroConcedidas,
  ItemCaixa,
  Legenda,
  ModuleBlock,
  ModuleHeader,
  ModuleTitle,
  PageBlock,
  PageBody,
  PageHeader,
  SearchInput,
  SecaoChips,
  SecaoTopo,
  SectionLabel,
  Toolbar,
} from "./styles.jsx";

function plural(n, singular, pluralTexto) {
  return `${n} ${n === 1 ? singular : pluralTexto}`;
}

function LegendaEstados({ allowRevoke }) {
  return (
    <Legenda aria-label="Legenda">
      <span>
        <ItemCaixa $state="checked" aria-hidden="true">✓</ItemCaixa> Liberada aqui
      </span>
      <span>
        <ItemCaixa $state="inherited" aria-hidden="true">✓</ItemCaixa> Vem do cargo
      </span>
      {allowRevoke && (
        <span>
          <ItemCaixa $state="revoked" aria-hidden="true">✕</ItemCaixa> Negada só para esta pessoa
        </span>
      )}
      <span>
        <ItemCaixa $state="off" aria-hidden="true" /> Não liberada
      </span>
      <span>Clique no ? para ver o que cada uma libera</span>
    </Legenda>
  );
}

function PermissionMatrix({
  catalogo,
  selecionados = new Set(),
  herdados = new Set(),
  revogados = new Set(),
  onToggle,
  allowRevoke = false,
  inheritedReadOnly = true,
  disabled = false,
  mostrarBuscaRapida = true,
  vazioMensagem = "Nenhuma permissão no catálogo.",
}) {
  // Quando allowRevoke=true, as que vem do cargo sao interativas (clicar ->
  // negar). Padrao mantem comportamento legado (somente leitura).
  const herdadasInterativas = allowRevoke || !inheritedReadOnly;
  const [busca, setBusca] = useState("");
  const [soConcedidas, setSoConcedidas] = useState(false);

  // `expandModulos === null` = "ainda nao inicializado" — o default
  // (colapsado exceto modulos com concessao) e calculado quando o catalogo
  // chega. Paginas defaultam a EXPANDIDAS quando o modulo abre (o mapa
  // guarda apenas o que o usuario mexeu).
  const [expandModulos, setExpandModulos] = useState(null);
  const [expandPaginas, setExpandPaginas] = useState({});
  // "Recolher tudo" muda o default para false: reabrir um modulo mostra as
  // paginas FECHADAS (pedido da gestora — antes tudo voltava aberto).
  const [paginasDefault, setPaginasDefault] = useState(true);

  const modulos = useMemo(
    () => (Array.isArray(catalogo?.modulos) ? catalogo.modulos : []),
    [catalogo]
  );
  const indice = useMemo(() => indexarPorCodigo(modulos), [modulos]);
  const setsConcessao = { selecionados, herdados, revogados };

  // Default de expansao: modulo aberto se ja tem alguma permissao liberada.
  // Roda uma unica vez por montagem — o guard impede re-colapsar enquanto o
  // gestor edita.
  useEffect(() => {
    if (!modulos.length || expandModulos !== null) return;
    const inicial = {};
    modulos.forEach((m) => {
      inicial[m.id] =
        contarConcedidas(permissoesDoModulo(m), {
          selecionados,
          herdados,
          revogados,
        }) > 0;
    });
    setExpandModulos(inicial);
  }, [modulos, expandModulos, selecionados, herdados, revogados]);

  const buscaAtiva = Boolean(normalizarBusca(busca));
  const filtroAtivo = buscaAtiva || soConcedidas;

  const modulosFiltrados = useMemo(() => {
    const porBusca = filtrarModulos(modulos, busca);
    return soConcedidas
      ? filtrarConcedidas(porBusca, { selecionados, herdados, revogados })
      : porBusca;
  }, [modulos, busca, soConcedidas, selecionados, herdados, revogados]);

  const toggleModulo = (id) => {
    setExpandModulos((prev) => {
      const atual = prev || {};
      return { ...atual, [id]: !(atual[id] ?? false) };
    });
  };

  const togglePagina = (chave) => {
    setExpandPaginas((prev) => ({
      ...prev,
      [chave]: !(prev[chave] ?? paginasDefault),
    }));
  };

  const definirTodos = (aberto) => {
    const todos = {};
    modulos.forEach((m) => {
      todos[m.id] = aberto;
    });
    setExpandModulos(todos);
    setExpandPaginas({});
    setPaginasDefault(aberto);
  };

  // Marca/desmarca TODAS as permissoes da secao que podem ser marcadas
  // aqui. As que vem do cargo ficam como estao (negar e item a item).
  const codigosMarcaveis = (permissoes) =>
    permissoes.map((p) => p.codigo).filter((c) => !herdados.has(c));

  const todasMarcadas = (permissoes) => {
    const codigos = codigosMarcaveis(permissoes);
    return codigos.length > 0 && codigos.every((c) => selecionados.has(c));
  };

  const alternarTodas = (permissoes) => {
    const marcar = !todasMarcadas(permissoes);
    codigosMarcaveis(permissoes).forEach((codigo) => {
      if (selecionados.has(codigo) !== marcar) onToggle?.(codigo, marcar);
    });
  };

  const chipProps = {
    selecionados,
    herdados,
    revogados,
    onToggle,
    herdadasInterativas,
    disabled,
    indice,
  };

  const totalLiberadas = contarConcedidas(Object.values(indice).map((i) => i.permissao), setsConcessao);
  const totalCatalogo = Object.keys(indice).length;

  const toolbar = mostrarBuscaRapida && (
    <Toolbar>
      <SearchInput
        type="search"
        placeholder="Buscar permissão (ex.: aprovar desconto, lote, nota fiscal)…"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        aria-label="Buscar permissão"
      />
      <FiltroConcedidas>
        <input
          type="checkbox"
          checked={soConcedidas}
          onChange={(e) => setSoConcedidas(e.target.checked)}
        />
        Só o que está liberado
      </FiltroConcedidas>
      <Counter>
        {totalLiberadas} de {totalCatalogo} liberadas
        {herdados.size > 0 && ` · ${plural(herdados.size, "vem", "vêm")} do cargo`}
        {revogados.size > 0 && ` · ${plural(revogados.size, "negada", "negadas")}`}
      </Counter>
      <BulkButton type="button" onClick={() => definirTodos(true)}>
        Expandir tudo
      </BulkButton>
      <BulkButton type="button" onClick={() => definirTodos(false)}>
        Recolher tudo
      </BulkButton>
    </Toolbar>
  );

  if (!modulosFiltrados.length) {
    return (
      <Container>
        {toolbar}
        <EmptyState>
          {buscaAtiva
            ? "Nenhuma permissão encontrada para essa busca."
            : soConcedidas
            ? "Nada liberado ainda."
            : vazioMensagem}
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {toolbar}
      <LegendaEstados allowRevoke={allowRevoke} />
      {modulosFiltrados.map((modulo) => {
        const permsModulo = permissoesDoModulo(modulo);
        const concedidasModulo = contarConcedidas(permsModulo, setsConcessao);
        // Filtro ativo forca expansao dos niveis com resultado.
        const moduloAberto = filtroAtivo || (expandModulos?.[modulo.id] ?? false);

        return (
          <ModuleBlock key={modulo.id}>
            <ModuleHeader
              type="button"
              aria-expanded={moduloAberto}
              onClick={() => toggleModulo(modulo.id)}
            >
              <ModuleTitle>
                <Chevron aria-hidden="true">{moduloAberto ? "▼" : "▶"}</Chevron>
                <span>{modulo.label}</span>
              </ModuleTitle>
              <CountBadge
                $ativo={concedidasModulo > 0}
                title={`${concedidasModulo} de ${permsModulo.length} liberadas`}
              >
                {concedidasModulo}/{permsModulo.length}
              </CountBadge>
            </ModuleHeader>

            {moduloAberto &&
              (modulo.paginas || []).map((pagina) => {
                const chavePagina = `${modulo.id}::${pagina.id}`;
                const permsPagina = permissoesDaPagina(pagina);
                const concedidasPagina = contarConcedidas(permsPagina, setsConcessao);
                const paginaAberta =
                  filtroAtivo || (expandPaginas[chavePagina] ?? paginasDefault);

                return (
                  <PageBlock key={pagina.id}>
                    <PageHeader
                      type="button"
                      aria-expanded={paginaAberta}
                      onClick={() => togglePagina(chavePagina)}
                    >
                      <ModuleTitle>
                        <Chevron aria-hidden="true">{paginaAberta ? "▼" : "▶"}</Chevron>
                        <span>{pagina.label}</span>
                      </ModuleTitle>
                      <CountBadge
                        $ativo={concedidasPagina > 0}
                        title={`${concedidasPagina} de ${permsPagina.length} liberadas`}
                      >
                        {concedidasPagina}/{permsPagina.length}
                      </CountBadge>
                    </PageHeader>

                    {paginaAberta && (
                      <PageBody>
                        {(pagina.secoes || []).map((secao) => {
                          const permissoes = secao.permissoes || [];
                          const rotulos = rotulosChipsDaSecao(permissoes);
                          const mostrarLabel = !esconderLabelSecao(pagina, secao);
                          const podeMarcarTodas =
                            !disabled && permissoes.length > 1 && codigosMarcaveis(permissoes).length > 0;
                          const nomeBloco = mostrarLabel ? secao.label : pagina.label;
                          const onde = `${modulo.label} › ${pagina.label}${
                            mostrarLabel ? ` › ${secao.label}` : ""
                          }`;
                          return (
                            <div key={secao.id}>
                              {(mostrarLabel || podeMarcarTodas) && (
                                <SecaoTopo>
                                  <SectionLabel>{mostrarLabel ? secao.label : ""}</SectionLabel>
                                  {podeMarcarTodas && (
                                    <BulkButton
                                      type="button"
                                      onClick={() => alternarTodas(permissoes)}
                                      aria-label={`${
                                        todasMarcadas(permissoes) ? "Desmarcar" : "Marcar"
                                      } todas as permissões de ${nomeBloco}`}
                                    >
                                      {todasMarcadas(permissoes) ? "Desmarcar todas" : "Marcar todas"}
                                    </BulkButton>
                                  )}
                                </SecaoTopo>
                              )}
                              <SecaoChips>
                                {permissoes.map((p) => (
                                  <PermissionChip
                                    key={p.codigo}
                                    permissao={p}
                                    rotulo={rotulos[p.codigo]}
                                    onde={onde}
                                    {...chipProps}
                                  />
                                ))}
                              </SecaoChips>
                            </div>
                          );
                        })}
                      </PageBody>
                    )}
                  </PageBlock>
                );
              })}
          </ModuleBlock>
        );
      })}
    </Container>
  );
}

export default PermissionMatrix;
